import { useState } from "react";
import { useGameStore, SCREEN } from "@/stores/gameStore";
import { useSettings } from "@/stores/settingsStore";
import { useT, LANGUAGES } from "@/i18n";
import { SETTINGS } from "@/constants/testIds";
import { useUiSounds } from "@/lib/audio";
import LobbyLayout from "@/components/LobbyLayout";
import { Monitor, Volume2, Gamepad2, Target, Globe, Settings as SettingsIcon } from "lucide-react";

export default function SettingsScreen() {
    const t = useT();
    const s = useSettings();
    const sfx = useUiSounds();
    const [tab, setTab] = useState("graphics");

    const TABS = [
        { id: "graphics", key: "settings.tab.graphics", icon: Monitor },
        { id: "audio", key: "settings.tab.audio", icon: Volume2 },
        { id: "controls", key: "settings.tab.controls", icon: Gamepad2 },
        { id: "gameplay", key: "settings.tab.gameplay", icon: Target },
        { id: "language", key: "settings.tab.language", icon: Globe },
    ];

    return (
        <LobbyLayout active={SCREEN.SETTINGS}>
            <div data-testid={SETTINGS.root} className="w-full h-full overflow-hidden flex flex-col px-8 py-6">
                <div className="mb-4">
                    <div className="axis-mono text-[10px] tracking-[0.5em] text-orange-300/80">
                        SYSTEM
                    </div>
                    <div className="axis-heading text-3xl tracking-[0.4em] text-white flex items-center gap-3">
                        <SettingsIcon size={22} className="text-orange-400" />
                        {t("settings.title")}
                    </div>
                </div>
                <div className="flex flex-1 overflow-hidden gap-4">
                    <div className="w-56 space-y-1.5 overflow-y-auto">
                        {TABS.map((tabDef) => {
                            const Icon = tabDef.icon;
                            const active = tab === tabDef.id;
                            return (
                                <button
                                    key={tabDef.id}
                                    data-testid={`${SETTINGS.tabPrefix}${tabDef.id}`}
                                    onClick={() => { sfx.click(); setTab(tabDef.id); }}
                                    className={`w-full axis-cut-sm px-4 py-3 flex items-center gap-3 text-left transition border ${
                                        active
                                            ? "bg-white/10 border-white text-white"
                                            : "border-transparent text-white/60 hover:text-white hover:bg-white/5"
                                    }`}
                                >
                                    <Icon size={15} />
                                    <span className="axis-heading text-sm tracking-widest">{t(tabDef.key)}</span>
                                </button>
                            );
                        })}
                    </div>
                    <div className="flex-1 overflow-y-auto axis-glass axis-cut-sm p-6 border border-white/10">
                        {tab === "graphics" && (
                            <div className="space-y-4 max-w-2xl">
                                <Select label="Display Mode" value={s.displayMode} onChange={(v) => s.update({ displayMode: v })} options={[
                                    { v: "fullscreen", l: "Fullscreen" }, { v: "borderless", l: "Borderless" }, { v: "windowed", l: "Windowed" }
                                ]} />
                                <Select label="Quality Preset" testId={SETTINGS.quality} value={s.quality} onChange={(v) => s.update({ quality: v })} options={[
                                    { v: "low", l: "Low" }, { v: "medium", l: "Medium" }, { v: "high", l: "High" }, { v: "ultra", l: "Ultra" }
                                ]} />
                                <Select label="Texture Quality" value={s.textureQuality} onChange={(v) => s.update({ textureQuality: v })} options={["low","medium","high","ultra"].map(v => ({ v, l: v[0].toUpperCase()+v.slice(1) }))} />
                                <Select label="Shadow Quality" value={s.shadowQuality} onChange={(v) => s.update({ shadowQuality: v })} options={["low","medium","high","ultra"].map(v => ({ v, l: v[0].toUpperCase()+v.slice(1) }))} />
                                <Select label="Lighting Quality" value={s.lightingQuality} onChange={(v) => s.update({ lightingQuality: v })} options={["low","medium","high","ultra"].map(v => ({ v, l: v[0].toUpperCase()+v.slice(1) }))} />
                                <Select label="Effects Quality" value={s.effectsQuality} onChange={(v) => s.update({ effectsQuality: v })} options={["low","medium","high","ultra"].map(v => ({ v, l: v[0].toUpperCase()+v.slice(1) }))} />
                                <Slider label="Render Distance" value={s.renderDistance} onChange={(v) => s.update({ renderDistance: v })} min={50} max={100} step={5} format={(v) => `${v}%`} />
                                <Slider label="Field of View" testId={SETTINGS.fov} value={s.fov} onChange={(v) => s.update({ fov: v })} min={60} max={110} step={1} format={(v) => `${v}°`} />
                                <Slider label="Frame Limit" value={s.frameLimit} onChange={(v) => s.update({ frameLimit: v })} min={0} max={240} step={30} format={(v) => v === 0 ? "Uncapped" : `${v} FPS`} />
                                <Slider label="Brightness" value={s.brightness} onChange={(v) => s.update({ brightness: v })} min={0.5} max={1.5} step={0.05} format={(v) => v.toFixed(2)} />
                                <Slider label="Gamma" value={s.gamma} onChange={(v) => s.update({ gamma: v })} min={0.5} max={1.5} step={0.05} format={(v) => v.toFixed(2)} />
                                <Toggle label="Shadows" value={s.shadows} onChange={(v) => s.update({ shadows: v })} />
                                <Toggle label="Anti-Aliasing" value={s.antialiasing} onChange={(v) => s.update({ antialiasing: v })} />
                                <Toggle label="Motion Blur" value={s.motionBlur} onChange={(v) => s.update({ motionBlur: v })} />
                                <Toggle label="Camera Shake" value={s.cameraShake} onChange={(v) => s.update({ cameraShake: v })} />
                                <Toggle label="VSync" value={s.vsync} onChange={(v) => s.update({ vsync: v })} />
                                <Toggle label="Show FPS Counter" value={s.showFps} onChange={(v) => s.update({ showFps: v })} />
                            </div>
                        )}
                        {tab === "audio" && (
                            <div className="space-y-4 max-w-2xl">
                                <Slider label="Master Volume" testId={SETTINGS.masterVolume} value={s.masterVolume} onChange={(v) => s.update({ masterVolume: v })} min={0} max={1} step={0.05} format={pct} />
                                <Slider label="Music" value={s.musicVolume} onChange={(v) => s.update({ musicVolume: v })} min={0} max={1} step={0.05} format={pct} />
                                <Slider label="Interface" value={s.interfaceVolume} onChange={(v) => s.update({ interfaceVolume: v })} min={0} max={1} step={0.05} format={pct} />
                                <Slider label="Weapon" value={s.weaponVolume} onChange={(v) => s.update({ weaponVolume: v })} min={0} max={1} step={0.05} format={pct} />
                                <Slider label="Footstep" value={s.footstepVolume} onChange={(v) => s.update({ footstepVolume: v })} min={0} max={1} step={0.05} format={pct} />
                                <Slider label="Voice" value={s.voiceVolume} onChange={(v) => s.update({ voiceVolume: v })} min={0} max={1} step={0.05} format={pct} />
                                <Slider label="Ambient" value={s.ambientVolume} onChange={(v) => s.update({ ambientVolume: v })} min={0} max={1} step={0.05} format={pct} />
                                <Toggle label="Mute when unfocused" value={s.muteWhenUnfocused} onChange={(v) => s.update({ muteWhenUnfocused: v })} />
                            </div>
                        )}
                        {tab === "controls" && (
                            <div className="space-y-4 max-w-2xl">
                                <Slider label="Mouse Sensitivity" testId={SETTINGS.sensitivity} value={s.mouseSensitivity} onChange={(v) => s.update({ mouseSensitivity: v })} min={0.1} max={3} step={0.05} format={(v) => v.toFixed(2)} />
                                <Slider label="ADS Sensitivity" value={s.adsSensitivity} onChange={(v) => s.update({ adsSensitivity: v })} min={0.1} max={2} step={0.05} format={(v) => v.toFixed(2)} />
                                <Slider label="Sniper Sensitivity" value={s.sniperSensitivity} onChange={(v) => s.update({ sniperSensitivity: v })} min={0.1} max={2} step={0.05} format={(v) => v.toFixed(2)} />
                                <Toggle label="Invert Mouse Y" value={s.invertMouse} onChange={(v) => s.update({ invertMouse: v })} />
                                <Toggle label="Raw Mouse Input" value={s.rawMouseInput} onChange={(v) => s.update({ rawMouseInput: v })} />
                                <Toggle label="Hold to Crouch (off = toggle)" value={s.holdToCrouch} onChange={(v) => s.update({ holdToCrouch: v })} />
                                <Toggle label="Hold to Aim (off = toggle)" value={s.holdToAim} onChange={(v) => s.update({ holdToAim: v })} />
                                <Toggle label="Quick Weapon Switch" value={s.weaponSwitchQuick} onChange={(v) => s.update({ weaponSwitchQuick: v })} />
                                <div className="axis-cut-sm border border-white/10 p-4">
                                    <div className="axis-heading text-sm text-white/80 mb-3 tracking-widest">
                                        KEY BINDINGS
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-xs axis-mono text-white/70">
                                        {["WASD:Move","Shift:Sprint","Ctrl:Crouch","Space:Jump","LMB:Fire","R:Reload","1:Rifle","2:SMG","3:Pistol","Esc:Pause","Tab:Scoreboard"].map((k, i) => {
                                            const [key, act] = k.split(":");
                                            return (
                                                <div key={i} className="flex items-center justify-between border-b border-white/5 py-1">
                                                    <span className="text-white/50">{act}</span>
                                                    <span className="px-2 py-0.5 border border-white/20 axis-cut-sm text-white/90">{key}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                        {tab === "gameplay" && (
                            <div className="space-y-4 max-w-2xl">
                                <ColorRow label="Crosshair Color" value={s.crosshairColor} onChange={(v) => s.update({ crosshairColor: v })} />
                                <Slider label="Crosshair Size" value={s.crosshairSize} onChange={(v) => s.update({ crosshairSize: v })} min={12} max={40} step={1} format={(v) => `${v}px`} />
                                <Toggle label="Hit Marker" value={s.hitMarker} onChange={(v) => s.update({ hitMarker: v })} />
                                <Toggle label="Damage Indicators" value={s.damageIndicators} onChange={(v) => s.update({ damageIndicators: v })} />
                                <Toggle label="Minimap Rotation" value={s.minimapRotation} onChange={(v) => s.update({ minimapRotation: v })} />
                                <Toggle label="Show Player Names" value={s.showPlayerNames} onChange={(v) => s.update({ showPlayerNames: v })} />
                                <Select label="Viewmodel Position" value={s.viewmodelPosition} onChange={(v) => s.update({ viewmodelPosition: v })} options={[{v:"left",l:"Left"},{v:"center",l:"Center"},{v:"right",l:"Right"}]} />
                                <Toggle label="Screen Effects" value={s.screenEffects} onChange={(v) => s.update({ screenEffects: v })} />
                                <Toggle label="Subtitles" value={s.subtitles} onChange={(v) => s.update({ subtitles: v })} />
                                <Toggle label="Play startup intro on launch" value={s.playIntroOnLaunch} onChange={(v) => s.update({ playIntroOnLaunch: v })} />
                            </div>
                        )}
                        {tab === "language" && (
                            <div className="space-y-3 max-w-md">
                                <div className="axis-mono text-[10px] tracking-[0.4em] text-white/50 mb-2">
                                    INTERFACE LANGUAGE
                                </div>
                                <div className="grid grid-cols-1 gap-2">
                                    {LANGUAGES.map((l) => (
                                        <button
                                            key={l.code}
                                            data-testid={`lang-${l.code}`}
                                            onClick={() => { sfx.click(); s.update({ language: l.code }); }}
                                            className={`axis-cut-sm px-4 py-3 border text-left transition ${
                                                s.language === l.code
                                                    ? "bg-white/10 border-white text-white"
                                                    : "border-white/10 bg-black/30 text-white/70 hover:text-white hover:border-white/40"
                                            }`}
                                        >
                                            <div className="axis-heading tracking-widest text-sm">{l.label}</div>
                                            <div className="axis-mono text-[10px] tracking-[0.3em] text-white/40 mt-0.5">
                                                {l.code.toUpperCase()}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </LobbyLayout>
    );
}

const pct = (v) => `${Math.round(v * 100)}%`;

function Row({ label, children }) {
    return (
        <div className="flex items-center justify-between gap-4 py-1.5">
            <label className="axis-mono text-xs tracking-widest text-white/70 min-w-[220px]">{label}</label>
            <div className="flex items-center gap-3">{children}</div>
        </div>
    );
}
function Slider({ label, value, onChange, min, max, step, format, testId }) {
    return (
        <Row label={label}>
            <input data-testid={testId} type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} className="w-56 accent-orange-400" />
            <div className="axis-hud text-lg text-orange-300 w-20 text-right">{format(value)}</div>
        </Row>
    );
}
function Toggle({ label, value, onChange }) {
    return (
        <Row label={label}>
            <button onClick={() => onChange(!value)} className={`w-14 h-7 axis-cut-sm border flex items-center px-1 transition ${value ? "bg-orange-400/30 border-orange-400" : "bg-white/5 border-white/20"}`}>
                <span className={`block w-5 h-5 axis-cut-sm bg-orange-300 transition-transform ${value ? "translate-x-6" : "translate-x-0"}`} />
            </button>
        </Row>
    );
}
function Select({ label, value, onChange, options, testId }) {
    return (
        <Row label={label}>
            <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="axis-mono bg-black/60 border border-white/20 axis-cut-sm px-3 py-2 text-sm text-white min-w-[180px] focus:border-orange-400 focus:outline-none">
                {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
        </Row>
    );
}
function ColorRow({ label, value, onChange }) {
    const swatches = ["#00e5ff", "#00ff66", "#ffb300", "#ff2a2a", "#ffffff"];
    return (
        <Row label={label}>
            <div className="flex gap-2">
                {swatches.map((c) => (
                    <button key={c} onClick={() => onChange(c)} style={{ background: c, borderColor: value === c ? "#fff" : "transparent" }} className="w-7 h-7 axis-cut-sm border-2" />
                ))}
            </div>
        </Row>
    );
}
